import { useState, useEffect } from "react";
import AdminTables from "../../adminTables";
import FileSaver from 'file-saver';
import createAxiosInstance from "../../../../utils/axiosConfig";
import { export_data } from "../../../../utils/endpoints";
import { useAuth } from "../../../../context/authContext";
import Alert from "../../alerts/alert";

// Helper function for robust UTC date parsing adapted for DD/MM/YYYY HH:mm:ss format
const parseUTCDate = (dateString: string): Date => {
	// Handle DD/MM/YYYY HH:mm:ss format (your actual format)
	const ddmmyyyyMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
	if (ddmmyyyyMatch) {
		const [, day, month, year, hour, minute, second] = ddmmyyyyMatch;
		
		// Create UTC date using Date.UTC() (month is 0-indexed in Date.UTC)
		const utcTime = Date.UTC(
			parseInt(year, 10),
			parseInt(month, 10) - 1, // Month is 0-indexed
			parseInt(day, 10),
			parseInt(hour, 10),
			parseInt(minute, 10),
			parseInt(second, 10)
		);
		
		return new Date(utcTime);
	}
	
	// Handle ISO format YYYY-MM-DDTHH:mm:ss (fallback for other formats)
	const timePart = dateString.split('T')[1] || '';
	const hasTimezoneInfo = timePart.includes('Z') || timePart.includes('+') || timePart.includes('-');
	
	if (hasTimezoneInfo) {
		return new Date(dateString);
	}
	
	const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
	if (isoMatch) {
		const [, year, month, day, hour, minute, second] = isoMatch;
		
		const utcTime = Date.UTC(
			parseInt(year, 10),
			parseInt(month, 10) - 1,
			parseInt(day, 10),
			parseInt(hour, 10),
			parseInt(minute, 10),
			parseInt(second, 10)
		);
		
		return new Date(utcTime);
	}
	
	// Final fallback
	console.warn('Unexpected date format:', dateString);
	return new Date(dateString);
};

interface ScoreHistoryEntry {
	date: string;
	data: Array<{
		category_id?: number;
		category_title?: string;
		domain_id?: number;
		domain_title?: string;
		total_score: number;
		active_domains_count?: number;
		completed_flows_count?: number;
	}>;
}

const ScoreHistory = ({
	isOpen,
	handleClose,
	loading,
	content = [],
	title = "Score History",
	isFramework = false,
	frameworkId=0,
}: {
	isOpen: boolean;
	handleClose: () => void;
	loading: boolean;
	content: ScoreHistoryEntry[];
	title?: string;
	isFramework?: boolean;
	frameworkId?: number;
}) => {
	const { userId, logout } = useAuth();
	const [visibleCount, setVisibleCount] = useState(10);
	const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);

	// Reset visible count when component opens
	useEffect(() => {
		if (isOpen) {
			setVisibleCount(10);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const handleDateTimeFormat = (date: string) => {
		try {
			// Use the robust UTC date parsing function
			const parsedDate = parseUTCDate(date);
			
			// Format using Intl.DateTimeFormat for consistent display
			return new Intl.DateTimeFormat("en-US", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "numeric",
				minute: "2-digit",
				hour12: true
			}).format(parsedDate);
		} catch (error) {
			console.warn('Error parsing date:', date, error);
			// Fallback to original string if parsing fails
			return date;
		}
	}

	const handleSeeMore = () => {
		setVisibleCount((prev) => prev + 10);
	};

	const handleExportData = async() => {
		if (!userId) window.location.href = "/";
		try {
			const scoreHistory = await createAxiosInstance().get(
				export_data(userId||0, frameworkId), {
					responseType: "blob", 
				  }
			);

			if (scoreHistory.status == 401 || scoreHistory.status == 403) {
				console.log("user token expired");
				logout();
				return;
			}

			if (scoreHistory.status == 500) {
				console.log("Trouble downloading the file. Please try again later.");
				setAlert({
					message: "Trouble downloading the file. Please try again later.",
					type: "danger",
					show: true,
				});
				return;
			}
			
			const contentDisposition = scoreHistory.headers["content-disposition"];
			let filename = "downloaded_file.xlsx"; // Default filename

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?(.+?)"?$/);
				filename = match?.[1] || filename;
			}
			const blob = new Blob([scoreHistory.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			  });

			FileSaver.saveAs(blob, filename.replace(/["']/g, ''));
		} catch (e) {
			console.log(e);
			setAlert({
				message: "Trouble downloading the file. Please try again later.",
				type: "danger",
				show: true,
			});
		}
	};

	const visibleData = content.slice(0, visibleCount);

	// Determine if we're showing domain data or framework data
	const isDomainData = content.length > 0 && content[0]?.data[0]?.domain_id !== undefined;
	const isFrameworkData = content.length > 0 && content[0]?.data[0]?.category_id !== undefined;

	const headerTextStyle = {
		color: "#183FCE",
		fontSize: '12px',
		fontWeight: 'bold' as const
	};

	const tableColumns = [
		{
			accessorKey: isDomainData ? "domain_title" : "category_title",
			width: "30%",
			header: () => <span style={headerTextStyle}>{isDomainData ? 'Domain' : 'Framework'}</span>,
			cell: ({ row }: { row: any }) => {
				const firstItem = row.original.data[0];
				const title = isDomainData ? firstItem?.domain_title : firstItem?.category_title;
				return <span style={{
					color: '#1E293B',
					fontSize: '0.95rem'
				}}>{title || ''}</span>;
			},
		},
	
		{
			id: "total_score",
			header: () => <span style={headerTextStyle}>Score</span>,
			width: "15%",
			cell: ({ row }: { row: any }) => {
				const firstItem = row.original.data[0];
				return <span style={{
					color: "#5376F0",
					fontWeight: 'bold',
					fontSize: '1rem'
				}}>{firstItem?.total_score ? firstItem.total_score.toFixed(2) : 'N/A'}</span>;
			},
		},
		{
			id: "assessment_date",
			header: () => <span style={headerTextStyle}>Date</span>,
			width: "35%",
			cell: ({ row }: { row: any }) => {
				return <span style={{
					fontSize: '0.9rem'
				}}>{handleDateTimeFormat(row.original.date)}</span>;
			},
		},
		{
			id: "count_column",
			header: () => <span style={headerTextStyle}>
				{isDomainData ? 'Completed Flows' : 'Active Domains'}
			</span>,
			width: "20%",
			cell: ({ row }: { row: any }) => {
				const firstItem = row.original.data[0];
				const count = isDomainData 
					? (firstItem?.completed_flows_count || 0)
					: (firstItem?.active_domains_count || 0);
				return <span style={{
					color: '#1E293B',
					fontSize: '0.95rem',
					// fontWeight: '400',
				}}>{count}</span>;
			},
		},
	];

	return (
		<>
			<div className="offcanvas-backdrop fade show" onClick={handleClose}></div>

			<div
				className="offcanvas offcanvas-end show"
				tabIndex={-1}
				id="offcanvasExample"
				aria-labelledby="offcanvasExampleLabel"
			>
				<div className="offcanvas-header">
					<h5 className="offcanvas-title" id="offcanvasExampleLabel">
						{title}
					</h5>
					<button
						type="button"
						className="btn-close"
						data-bs-dismiss="offcanvas"
						aria-label="Close"
						onClick={handleClose}
					></button>
				</div>

			<div className="offcanvas-body" style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
				{loading ? (
					<div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				) : (
					<>
						{/* Fixed Header Section */}
						<div className="px-3 w-100" style={{ 
							paddingTop: '1rem',
							paddingBottom: '0.5rem',
							borderBottom: '1px solid #e0e0e0',
							backgroundColor: '#fff'
						}}>
							<div className={`d-flex mb-2 ${isFramework ? 'justify-content-between' : 'justify-content-end'}`} >
								{
									isFramework && content.length > 0 && (
										<div className="text-center text-muted">
											<button 
												className="btn btn-primary" 
												onClick={handleExportData}
												style={{
													transition: 'all 0.5s ease'
												}}
												onMouseEnter={(e) => {
													const target = e.target as HTMLButtonElement;
													target.style.backgroundColor = '#26397E';
													target.style.color = "white";
													target.style.transform = 'translateY(-2px)';
												}}
												onMouseLeave={(e) => {
													const target = e.target as HTMLButtonElement;
													target.style.backgroundColor = '#9ecbff';
													target.style.color = "black";
													target.style.transform = 'translateY(0)';
												}}
											>
												Export Framework Scores &nbsp; <i className="bi bi-download"></i>
											</button>
										</div>
									)
								}
								<div className="d-flex justify-content-end">
									<p style={{
										backgroundColor: '#ABCAFF',
										padding: '0.5rem 1rem',
										fontWeight: 'bold',
										fontSize: '1rem',
										borderRadius: '14px',
										margin: 0
									}}>
										Total Count: {content.length}
									</p>
								</div>
							</div>
						</div>

						{/* Scrollable Table Section */}
						<div className="px-3 w-100 scroller" style={{ 
							flex: 1, 
							overflowY: 'auto',
							overflowX: 'hidden'
						}}>
							{content.length === 0 ? (
								<div className="text-center text-muted mt-4">
									<p>No score history available yet.</p>
								</div>
							) : (
								<>
									<div style={{ marginTop: '1rem' }}>
										<AdminTables
											data={visibleData}
											columns={tableColumns}
											rowSelectionEnabled={false}
											serverSideSearch={false}
											showSearch={false}
											onRowSelect={()=>{}}
											containerStyle={
												{
													width: "100%",
													overflowX: "auto"
												}
											}
										/>
									</div>
									
									{content.length > visibleCount && (
										<div className="text-center mt-3 mb-3">
											<button 
												className="btn btn-outline-primary"
												onClick={handleSeeMore}
												style={{
													padding: '0.5rem 2rem',
													fontWeight: 'bold'
												}}
											>
												See More
											</button>
										</div>
									)}
								</>
							)}
						</div>
					</>
				)}

				{alert && (
					<Alert
					message={alert.message}
					type={alert.type}
					show={alert.show}
					onClose={() => setAlert(null)}
					/>
				)}
				
			</div>
				
			</div>

			
		</>
	);
};

export default ScoreHistory;
